/**
 * Shared marketing-site button.
 *
 * Single source of truth for every primary/ghost CTA on the public
 * marketing pages (Nav, Hero, TryIt, Pricing, Contact, BottomCTA,
 * PainPoints, Process, FAQ). The admin app is intentionally OUT of
 * scope for this component — it keeps its own navy-primary language.
 *
 * Design contract:
 *  - Primary: champagne gold gradient (#D4A65A → #E6BE7A), navy text,
 *    subtle inner ring + lifted shadow. Hover flips the gradient and
 *    intensifies the shadow. Never uses the bright #FFD54F yellow —
 *    the brand tone is premium champagne, not neon.
 *  - Ghost: transparent with a hairline border that warms to champagne
 *    on hover. For secondary CTAs on the dark marketing background.
 *  - Sizes sm/md/lg share the same `rounded-lg`, same weight (semibold),
 *    same font-family — only padding and font-size change.
 *
 * Rendering:
 *   <Button as="a" href="#tryit" variant="primary" size="lg" data-testid="...">
 *     Begin assessment <ArrowRight />
 *   </Button>
 */
import { forwardRef } from "react";

const BASE =
  "inline-flex items-center justify-center gap-2 font-semibold rounded-lg " +
  "transition-all duration-200 focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-[#F0D497] focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-[#050B18] disabled:cursor-not-allowed";

const VARIANTS = {
  // Solid champagne — no gradient. Every size (sm/md/lg) renders the
  // identical pixel colour top-to-bottom. Base is set to the bright
  // saturated hover-tone so the button reads "premium bright gold" at
  // rest, and the box-shadow glow is size-proportional (larger blur
  // radius) so a 51px lg button glows as prominently as a 35px sm one.
  // Solid champagne — no gradient, no opacity dimming. Every size and
  // every state (enabled/disabled/hover) renders the identical pixel
  // colour so nav-sm, hero-md, and hero-lg buttons match perceptually.
  // Bigger outer glow so the "shine" halo is prominent on all sizes.
  primary:
    "text-[#0B1220] font-semibold " +
    "bg-[#FBD46A] hover:bg-[#FFE28A] " +
    "ring-1 ring-inset ring-[#FFF3C5]/95 " +
    "shadow-[inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(139,92,25,0.22),0_18px_60px_-4px_rgba(251,212,106,1)] " +
    "hover:shadow-[inset_0_1px_0_rgba(255,255,255,1),inset_0_-1px_0_rgba(139,92,25,0.25),0_24px_72px_-4px_rgba(255,226,138,1)] " +
    "disabled:shadow-none disabled:cursor-not-allowed",
  // Hairline ring instead of a real border, matching primary's
  // "height-independent by construction" trick — a `border` adds to the
  // box (padding-box + border-box), so a bare `border` here made ghost
  // buttons render 2px taller than primary/other ghost buttons of the
  // same size. `ring` is a box-shadow: same 1px hairline, zero box growth.
  ghost:
    "text-slate-200 hover:text-white " +
    "bg-transparent ring-1 ring-inset ring-white/15 hover:ring-[#D4A65A]/60 " +
    "disabled:opacity-40",
};

const SIZES = {
  sm: "px-3.5 py-2 text-[12.5px]",
  md: "px-5 py-2.5 text-[13.5px]",
  lg: "px-7 py-3.5 text-[15px]",
};

export const Button = forwardRef(function Button(
  {
    as = "button",
    variant = "primary",
    size = "md",
    className = "",
    children,
    ...rest
  },
  ref,
) {
  const Tag = as;
  const cls =
    `${BASE} ${VARIANTS[variant] || VARIANTS.primary} ${SIZES[size] || SIZES.md} ${className}`.trim();
  return (
    <Tag ref={ref} className={cls} {...rest}>
      {children}
    </Tag>
  );
});

export default Button;
